import { Image } from 'expo-image'
import { useTranslation } from 'react-i18next'
import { useWindowDimensions, View } from 'react-native'
import Animated, { FadeIn, FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { COLORS, SPACING } from '../../lib/brand'
import { RADIUS, DURATION, STAGGER, TEXT_STYLES, useSemanticColors } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { GlassSurface } from '../ui/GlassSurface'

/**
 * Narrowest canvas that can hold the brand panel and a form column side by
 * side without either one becoming a gutter. Unchanged — it was never the
 * wrong number, only ever half of the question.
 */
const SPLIT_MIN_WIDTH = 900

/**
 * Does this canvas get the two-column brand composition?
 *
 * Width alone used to decide, and width alone is what put a landscape-desktop
 * layout onto a portrait iPad. A 13" iPad hands the auth segment a 1032x1376
 * canvas: it clears 900, so it was split into a 516pt form column beside a
 * 516pt brand panel that had 1376pt of height to fill with a logo, a headline
 * and a rule — roughly 700pt of empty canvas above and below the lockup. That
 * is the shape of the first screenshot on the App Store listing.
 *
 * The two-column split is a *landscape* idiom: it trades horizontal room, of
 * which a wide window has a surplus, for vertical room, of which it is short.
 * A portrait tablet has exactly the opposite budget, so it gets the
 * single-column composition instead — the same one the phone gets, at a
 * tablet's scale. Orientation is therefore part of the predicate.
 *
 * This is deliberately NOT the same question as `width >= 900` in
 * `AuthScaffold` and the phone-auth screens. Theirs asks "is this canvas wide
 * enough that a full-bleed form would sprawl, so the body should sit in a
 * centred column?" — which stays true on a portrait iPad, and stays untouched.
 * This one asks "are there two columns?", and only that one needed the
 * orientation term. Both `app/(auth)/_layout.tsx` (which renders the panel)
 * and `app/(auth)/welcome.tsx` (which must render the opposite column, and
 * nothing else, when the panel is up) read this hook so the two can never
 * disagree and paint two brand panels or none.
 */
export function useAuthBrandSplit(): boolean {
  const { width, height } = useWindowDimensions()
  return width >= SPLIT_MIN_WIDTH && width > height
}

interface BrandPanelProps {
  headline?: string
  subtitle?: string
}

/**
 * Shared measure for the headline and the subtitle. The subtitle was capped
 * and the headline was not, so on a wide desktop the two halves of one block
 * broke on different column widths.
 */
const MEASURE = 360

export function BrandPanel({ headline, subtitle }: BrandPanelProps) {
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()
  // The right half of the split auth layout is theme-aware (AuthScaffold reads
  // useTheme), so hardcoding this half light produced a screen that was
  // literally half white and half dark — on the first screen of the app.
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING['2xl'],
        borderRightWidth: 1,
        borderRightColor: colors.border,
        overflow: 'hidden',
      }}
    >
      {/* Centred editorial lockup: mark, headline and slogan share one
          vertical axis, and the block itself stays in the middle of the
          panel — not glued to the gutter, not left-weighted. */}
      <GlassSurface
        tone="quiet"
        visualRole="surface"
        radius={RADIUS.xl}
        style={{ width: '100%', maxWidth: MEASURE }}
        contentStyle={{ alignItems: 'center', padding: SPACING['2xl'] }}
      >
        <Animated.View
          collapsable={false}
          entering={reducedMotion ? undefined : FadeIn.duration(DURATION.slow)}
        >
          <Image
            source={require('../../assets/images/logo-stacked.png')}
            style={{ width: 132, height: 132 }}
            contentFit="contain"
            accessibilityLabel={t('common.app_name')}
          />
        </Animated.View>
        {headline ? (
          <Animated.Text
            entering={
              reducedMotion ? undefined : FadeInDown.duration(DURATION.slow).delay(STAGGER * 2)
            }
            style={{
              // Was 34/42/0 — off the type scale entirely, sitting between h1
              // (32) and displayBody (40) and tracking at 0 while every other
              // Bold display line tracks negative. `displayBody` is the token
              // for a display line in the body face; the brand poster on the
              // widest breakpoint in the app should be set with it.
              ...TEXT_STYLES.displayBody,
              marginTop: SPACING.xl,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {headline}
          </Animated.Text>
        ) : null}
        {subtitle ? (
          <Animated.Text
            entering={
              reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
            }
            style={{
              ...TEXT_STYLES.body,
              marginTop: SPACING.md,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </Animated.Text>
        ) : null}
        <Animated.View
          collapsable={false}
          entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal).delay(STAGGER * 6)}
          style={{
            width: 48,
            height: 3,
            borderRadius: RADIUS.xs,
            backgroundColor: COLORS.coral.DEFAULT,
            marginTop: SPACING.xl,
          }}
        />
      </GlassSurface>
    </View>
  )
}
